import React, { useRef } from 'react';
import PropTypes from 'prop-types';
import { makeStyles, colors, TextField, Typography, Grid, Card, CardContent } from '@material-ui/core';
import Avatar from '@material-ui/core/Avatar';
import { profileURL } from 'config';
import AvatarImage from 'assets/images/seul.png';
import { injectIntl, FormattedMessage } from 'react-intl';
import { hasPhoneValidationError, getPhoneValidationHelperText } from 'utils/validations';
import MuiPhoneNumber from 'material-ui-phone-number';
import { ReactComponent as IconPhoto } from 'assets/svgs/add_photo.svg';
import { showMessage } from 'utils';

const avatarSize = 70;

const useStyles = makeStyles(theme => ({
  rounded: {
    backgroundColor: 'white',
    padding: 4,
    width: avatarSize,
    height: avatarSize,
    marginBottom: theme.spacing(2),
    cursor: 'pointer',
  },
  // responsive tweaks for small screens
  '@media (max-width:600px)': {
    rounded: {
      width: avatarSize - 8,
      height: avatarSize - 8,
      marginBottom: theme.spacing(1),
      img: {
        width: avatarSize - 8,
        height: avatarSize - 8,
      },
      title: {
        fontSize: theme.typography.pxToRem(16)
      },
      subtitle: {
        fontSize: theme.typography.pxToRem(13)
      }
    },
  },
  img: {
    width: avatarSize,
    height: avatarSize,
    backgroundColor: colors.grey[300],
    borderRadius: 10
  },
  hidden: {
    display: 'none',
  },
  field: {
    flex: 1
  },
  phoneInput: {
    flex: 1,
    '& .MuiOutlinedInput-root': {
      backgroundColor: 'white',
    },
    '& .MuiFormHelperText-root': {
      backgroundColor: 'transparent',
    },
  },
  icon: {
    marginRight: theme.spacing(1),
    width: 15,
    height: 15,
    fill: colors.grey[900],
    '& path': {
      fill: colors.grey[900],
    },
  },
  horizon: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(1),
  },
  title: {
    color: theme.palette.app.primary,
    marginBottom: theme.spacing(1),
    fontWeight: '600',
    textTransform: 'uppercase'
  },
  email: {
    transform: 'translate(0, 1.5px) scale(0.75)',
    transformOrigin: 'top left',
    lineHeight: '10px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: 15,
    padding: '16px !important'
  },
  label: {
    fontWeight: 'bold',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(0.5)
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    borderBottom: '1px solid #E5E3F2',
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'center',
      textAlign: 'center'
    }
  },
  subtitle: {
    color: '#0E1927'
  },
  photoIcon: {
    width: 30,
    height: 30
  }
}));

const ProfileBasic = ({
  data,
  handleChange,
  email,
  setFormState,
  intl: { formatMessage },
  formState
}) => {
  const { picture, firstName, lastName, position, file, fileSrc, phone } = data;
  const classes = useStyles();

  const isOnlineImage = picture !== undefined && picture !== '';
  const url = profileURL + picture;

  const fileInputRef = useRef();

  // Allowed file types
  const ALLOWED_EXTENSIONS = ['.jpg', '.jpeg', '.png', '.gif', '.webp'];
  const ALLOWED_MIME_TYPES = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
  const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB in bytes

  // Magic bytes (file signatures) for image validation
  const IMAGE_MAGIC_BYTES = {
    'image/jpeg': [0xFF, 0xD8, 0xFF],
    'image/png': [0x89, 0x50, 0x4E, 0x47],
    'image/gif': [0x47, 0x49, 0x46, 0x38],
    'image/webp': [0x52, 0x49, 0x46, 0x46], // RIFF header, needs additional check
  };

  /**
   * Validate file extension
   */
  const validateFileExtension = (fileName) => {
    const extension = fileName.toLowerCase().substring(fileName.lastIndexOf('.'));
    return ALLOWED_EXTENSIONS.includes(extension);
  };

  /**
   * Validate MIME type
   */
  const validateMimeType = (mimeType) => {
    return ALLOWED_MIME_TYPES.includes(mimeType);
  };

  /**
   * Validate file size
   */
  const validateFileSize = (fileSize) => {
    return fileSize <= MAX_FILE_SIZE;
  };

  /**
   * Validate magic bytes (file signature) to prevent file type spoofing
   */
  const validateMagicBytes = (file, expectedMimeType) => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const arrayBuffer = e.target.result;
        const bytes = new Uint8Array(arrayBuffer);
        const magicBytes = IMAGE_MAGIC_BYTES[expectedMimeType];

        if (!magicBytes) {
          resolve(false);
          return;
        }

        // Check if file starts with expected magic bytes
        let isValid = true;
        for (let i = 0; i < magicBytes.length; i++) {
          if (bytes[i] !== magicBytes[i]) {
            isValid = false;
            break;
          }
        }

        // Additional check for WebP (RIFF...WEBP)
        if (expectedMimeType === 'image/webp' && isValid) {
          // Check for WEBP string at offset 8
          const webpString = String.fromCharCode(...bytes.slice(8, 12));
          isValid = webpString === 'WEBP';
        }

        resolve(isValid);
      };
      reader.onerror = () => resolve(false);
      // Read first 12 bytes for magic byte validation
      reader.readAsArrayBuffer(file.slice(0, 12));
    });
  };

  /**
   * Validate uploaded file
   */
  const validateFile = async (file) => {
    // Validate file extension
    if (!validateFileExtension(file.name)) {
      showMessage({
        message: formatMessage({ id: 'profile.avatar.invalid_extension' }) || 
          `Invalid file type. Allowed extensions: ${ALLOWED_EXTENSIONS.join(', ')}`,
        type: 'danger',
      });
      return false;
    }

    // Validate MIME type
    if (!validateMimeType(file.type)) {
      showMessage({
        message: formatMessage({ id: 'profile.avatar.invalid_mime' }) || 
          `Invalid file type. Allowed types: ${ALLOWED_MIME_TYPES.join(', ')}`,
        type: 'danger',
      });
      return false;
    }

    // Validate file size
    if (!validateFileSize(file.size)) {
      showMessage({
        message: formatMessage({ id: 'profile.avatar.file_too_large' }) || 
          `File size exceeds maximum allowed size of ${MAX_FILE_SIZE / (1024 * 1024)}MB`,
        type: 'danger',
      });
      return false;
    }

    // Validate magic bytes (file signature)
    const isValidMagicBytes = await validateMagicBytes(file, file.type);
    if (!isValidMagicBytes) {
      showMessage({
        message: formatMessage({ id: 'profile.avatar.invalid_file_signature' }) || 
          'Invalid file signature. File may be corrupted or not a valid image.',
        type: 'danger',
      });
      return false;
    }

    return true;
  };

  const onAvatar = () => {
    fileInputRef.current.click();
  };

  const onFileChange = async (event) => {
    if (event.target.files.length > 0) {
      event.persist();

      const file = event.target.files[0];

      // Validate file before processing
      const isValid = await validateFile(file);
      if (!isValid) {
        // Reset file input
        event.target.value = '';
        return;
      }

      const reader = new FileReader();
      reader.onload = function (e) {
        let img = new Image();
        img.onload = () => {
          setFormState({
            ...formState,
            file,
            fileSrc: reader.result,
          });
        };
        img.onerror = () => {
          showMessage({
            message: formatMessage({ id: 'profile.avatar.invalid_image' }) || 
              'Invalid image file. Please select a valid image.',
            type: 'danger',
          });
          event.target.value = '';
        };
        img.src = reader.result;
      };
      reader.onerror = () => {
        showMessage({
          message: formatMessage({ id: 'profile.avatar.read_error' }) || 
            'Error reading file. Please try again.',
          type: 'danger',
        });
        event.target.value = '';
      };
      reader.readAsDataURL(file);
    }
  };

  const isError = value => value === undefined || value === '';

  let src = AvatarImage;
  const phoneHasError = isError(phone) || hasPhoneValidationError(phone);

  if (file) src = fileSrc;
  else if (isOnlineImage) src = url;

  return (
    <Grid item xs={12} sm={12} md={6}>
      <Card className={classes.card}>
        <CardContent>
          <div className={classes.header}>
            <div>
              <Typography variant="h4" className={classes.title}>
                <FormattedMessage id="my_information" />
              </Typography>
              <Typography variant="h6" className={classes.subtitle}>
                {formatMessage({ id: "profile.basic.description" })}
              </Typography>
            </div>
            <Avatar variant="rounded" className={classes.rounded} onClick={onAvatar}>
              {
                (file || isOnlineImage) ?
                  <img
                    src={src}
                    alt="avatar"
                    className={classes.img}
                  /> :
                  <IconPhoto className={classes.photoIcon} />
              }
            </Avatar>
            <input
              type="file"
              className={classes.hidden}
              id="avatar-file"
              ref={fileInputRef}
              onChange={onFileChange}
              accept="image/png,image/jpeg,image/jpg,image/gif,image/webp"
            />
          </div>
          <div>
            <Grid container display="flex" spacing={4}>
              <Grid item xs={12} sm={12} md={6}>
                <Typography className={classes.label}>Identifiant Email</Typography>
                <TextField
                  value={email}
                  disabled
                  className={classes.field}
                  variant="outlined"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Typography className={classes.label}>{formatMessage({ id: 'phone' })}</Typography>
                <MuiPhoneNumber
                  className={classes.phoneInput}
                  defaultCountry={'fr'}
                  onChange={value => {
                    handleChange({
                      target: {
                        name: 'phone',
                        type: 'text',
                        value,
                      },
                    });
                  }}
                  value={phone}
                  error={phoneHasError}
                  helperText={phoneHasError && getPhoneValidationHelperText(phone, {
                    emptyText: formatMessage({ id: 'phone' }),
                    formatMessage,
                  })}
                  variant="outlined"
                  fullWidth
                />
              </Grid>
            </Grid>
          </div>

          <div>
            <Grid container display="flex" spacing={4}>
              <Grid item xs={12} sm={12} md={6}>
                <Typography className={classes.label}>{formatMessage({ id: 'first_name' })}</Typography>
                <TextField
                  name="firstName"
                  value={firstName}
                  onChange={handleChange}
                  error={isError(firstName)}
                  className={classes.field}
                  variant="outlined"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>
                <Typography className={classes.label}>{formatMessage({ id: 'last_name' })}</Typography>
                <TextField
                  className={classes.field}
                  name="lastName"
                  value={lastName}
                  onChange={handleChange}
                  error={isError(lastName)}
                  variant="outlined"
                  fullWidth
                />
              </Grid>
            </Grid>
          </div>
          <div>
            <Grid container display="flex" spacing={4}>
              <Grid item xs={12} sm={12} md={6}>
                <Typography className={classes.label}>{formatMessage({ id: 'position' })}</Typography>
                <TextField
                  className={classes.field}
                  name="position"
                  value={position}
                  onChange={handleChange}
                  error={isError(position)}
                  variant="outlined"
                  fullWidth
                />
              </Grid>
              <Grid item xs={12} sm={12} md={6}>

              </Grid>
            </Grid>
          </div>
        </CardContent>
      </Card>
    </Grid>
  );
};

ProfileBasic.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }),
  data: PropTypes.shape({
    picture: PropTypes.any,
    firstName: PropTypes.string,
    lastName: PropTypes.string,
    position: PropTypes.string,
    phone: PropTypes.string,
    file: PropTypes.any,
    fileSrc: PropTypes.any,
  }),
  handleChange: PropTypes.func.isRequired,
  setFormState: PropTypes.func.isRequired,
  email: PropTypes.string,
  formState: PropTypes.any
};

export default injectIntl(ProfileBasic);
